import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SEOHead from "../components/SEOHead";
import Footer from "../components/Footer";
import { HomeNav } from "../components/home/HomeNav";
import { HeroSection } from "../components/home/HeroSection";
import { TopDealsSection, DealScoreSection } from "../components/home/DealSections";
import { HowItWorksSection, FeaturesSection, RealtorSection } from "../components/home/ProductSections";
import { AlertsSection, PricingSection, GuidesSection, FinalCTASection } from "../components/home/ConversionSections";
import { trackCTAClick } from "../lib/tracking";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "RealixData",
    "url": "https://realixdata.com",
    "logo": "https://realixdata.com/logo.png",
    "description": "RealixData is a real estate investment analysis platform. Instant cash flow, cap rate, ROI, and deal scoring for rental properties across the US.",
    "sameAs": ["https://twitter.com/RealixData"],
    "foundingDate": "2026",
    "knowsAbout": ["Real Estate Investment", "Rental Property Analysis", "Cash Flow Analysis", "Cap Rate", "BRRRR Strategy", "Deal Scoring"],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "RealixData",
    "url": "https://realixdata.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": "https://realixdata.com/dashboard?q={search_term_string}" },
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://realixdata.com/" }],
  },
];

export default function LandingPage({ isAuthenticated = false }) {
  const navigate = useNavigate();
  const [topDeals, setTopDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [dealsError, setDealsError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Single fetch shared by the hero preview, deal cards and report preview.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/top-deals?limit=6`)
      .then((r) => { if (alive) setTopDeals(r.data?.deals || []); })
      .catch(() => { if (alive) setDealsError(true); })
      .finally(() => { if (alive) setDealsLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackCTAClick("hero_search", "/dashboard");
      navigate(`/dashboard?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Existing property navigation: Dashboard auto-loads ?property=ID.
  const openDeal = (deal) => {
    const id = deal?.property?.id;
    trackCTAClick("deal_card", id ? `/dashboard?property=${id}` : "/top-deals");
    navigate(id ? `/dashboard?property=${id}` : "/top-deals");
  };
  const go = (cta, to) => { trackCTAClick(cta, to); navigate(to); };

  const heroDeal = topDeals[0] || null;
  const cardDeals = topDeals.length > 3 ? topDeals.slice(1, 4) : topDeals.slice(0, 3);

  return (
    <div className="rd-page min-h-screen" data-testid="landing-page">
      <SEOHead
        title="RealixData | See Cash Flow, ROI & Deal Score for Any Property in Seconds"
        description="Instantly analyze rental properties — cash flow, cap rate, ROI, and deal score. Based on real market data. Used by investors to evaluate deals in seconds. No spreadsheets required."
        canonical="/"
        jsonLd={JSON_LD}
      />

      <HomeNav isAuthenticated={isAuthenticated} />

      <main>
        <HeroSection
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
          topDeal={heroDeal}
          dealsLoading={dealsLoading}
          onOpenDeal={() => openDeal(heroDeal)}
        />
        <TopDealsSection
          deals={cardDeals}
          loading={dealsLoading}
          error={dealsError}
          onOpenDeal={openDeal}
          onSeeAll={() => go("see_all_deals", "/top-deals")}
        />
        <DealScoreSection onLearnMore={() => go("scoring_learn_more", "/how-scoring-works")} />
        <HowItWorksSection onCta={() => go("how_it_works_cta", isAuthenticated ? "/dashboard" : "/auth")} />
        <FeaturesSection />
        <RealtorSection deal={topDeals[1] || heroDeal} onCta={() => go("create_report_cta", "/create-report")} />
        <AlertsSection />
        <PricingSection isAuthenticated={isAuthenticated} navigate={navigate} />
        <GuidesSection />
        <FinalCTASection
          onAnalyze={() => go("bottom_cta", isAuthenticated ? "/dashboard" : "/auth")}
          onFindDeals={() => go("bottom_cta_find_deals", "/top-deals")}
        />
      </main>

      <Footer variant="dark" />
    </div>
  );
}
